/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import API from 'utils/api';
import getFormattedValues from 'utils/formatValues';
import { onError } from 'utils/handlers';
import { ESTATUS_AUTORIZADO, ESTATUS_CANCELADO } from 'utils/constants';

export const baseURI = 'nominas/nominas';

const getValuesAndConfig = (values) => {
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };
  const formattedValues = getFormattedValues(values, {
    formData: config.headers,
    clean: true,
  });
  return [formattedValues, config];
};

/**
 * GET Calculos De Nominas
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const getCalculosDeNominas = async (params) => {
  try {
    const response = await API.get(baseURI, { params });
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Calculo De Nomina
 * @param {number} id
 * @returns {Promise<object|null>}
 */
export const getCalculoDeNomina = async (id) => {
  try {
    const response = await API.get(`${baseURI}/${id}`);
    return response.data;
  } catch (err) {
    onError(err);
  }
  return null;
};

export const postCalculosDeNominas = async (values) => {
  try {
    const payload = {
      ...values,
      folio_agrupador: 'CON',
      numero_plazas_calculadas: 1,
      numero_nomina: 'N-13',
    };

    const formData = new FormData();

    Object.entries(payload).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        // 👇 CLAVE: enviar arrays correctamente
        value.forEach((v) => {
          formData.append(key, v);
        });
      } else if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });

    const response = await API.post(baseURI, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * PATCH Autorizar Nomina
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const autorizarNomina = async (id) => {
  try {
    const response = await API.patch(`${baseURI}/${id}`, { estados_globales: ESTATUS_AUTORIZADO });
    return response;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
   * PATCH Cancelar Nomina
   * @param {object} values
   * @returns {Promise<object|boolean>}
   */
export const cancelarNomina = async (id) => API.patch(`nominas/nominas/${id}`, { estados_globales: ESTATUS_CANCELADO });

/**
 * GET Nominas Plazas Percepciones
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const getNominasPlazasPercepciones = async (params) => {
  try {
    const response = await API.get('nominas/nominas-plazas-percepciones', { params });
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Nominas Plazas Deducciones
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const getNominasPlazasDeducciones = async (params) => {
  try {
    const response = await API.get('nominas/nominas-plazas-deducciones', { params });
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Nominas Plazas Descuentos
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const getNominasPlazasDescuentos = async (params) => {
  try {
    const response = await API.get('nominas/nominas-plazas-descuentos', { params });
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Nominas Errores
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const getNominasErrores = async (params) => {
  try {
    const response = await API.get('nominas/nominas-errores', { params });
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Estado de una tarea Celery por su task_id (usado para hacer polling de
 * descuentos_saldo_task_id, el id que regresa Autorizar/Cancelar mientras afecta
 * en segundo plano el saldo de los descuentos con control de saldo).
 * @param {string} taskId
 * @returns {Promise<object|null>}
 */
export const getTaskStatus = async (taskId) => {
  try {
    const response = await API.get(`nominas/task-status/${taskId}`);
    return response.data;
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * GET si hay un Calculo/Autorizacion/Cancelacion de nomina en curso para la entidad
 * del usuario (ver nominas/utils/bloqueo_periodo_nomina.py). Usado para deshabilitar
 * Calcular/Autorizar/Cancelar y mostrar un indicador mientras dure, en vez de que el
 * usuario descubra el bloqueo hasta que la acción falla.
 * @returns {Promise<{en_curso: boolean}|null>}
 */
export const getEstadoBloqueoEntidad = async () => {
  try {
    const response = await API.get(`${baseURI}/estado-bloqueo/`);
    return response.data;
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * POST Detener el cálculo de una nómina en curso
 * @param {number} id
 * @returns {Promise<object|boolean>}
 */
export const postDetenerCalculo = async (id) => {
  try {
    const response = await API.post(`nominas/nominas/${id}/detener-calculo/`);
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * GET Progreso de cálculo en tiempo real (cuenta NominaPlaza con eliminado=false)
 * @param {number} id
 * @returns {Promise<object|null>}
 */
export const getProgresoCalculo = async (id) => {
  try {
    const response = await API.get(`nominas/nominas/${id}/progreso-calculo/`);
    return response.data;
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * POST Recalcular Nomina (todos los empleados)
 * @param {number} id
 * @returns {Promise<object|boolean>}
 */
export const recalcularNomina = async (id) => {
  try {
    const response = await API.post(`nominas/nominas/${id}/recalcular/`);
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * PATCH Guardar campos de cálculo por empleado
 * @param {number} id
 * @param {object} values
 * @returns {Promise<object|boolean>}
 */
export const patchCalculoDeNomina = async (id, values) => {
  try {
    const response = await API.patch(`${baseURI}/${id}`, values);
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * POST Recalcular Nomina solo para los empleados seleccionados
 * @param {number} id
 * @returns {Promise<object|boolean>}
 */
export const recalcularNominaEmpleados = async (id) => {
  try {
    const response = await API.post(`nominas/nominas/${id}/recalcular-empleados/`);
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * POST Validar (sin persistir) el layout de Anticipo de Aguinaldo cargado
 * @param {File} file
 * @returns {Promise<object|boolean>}
 */
export const postValidarLayoutAnticipoAguinaldo = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await API.post(`${baseURI}/anticipo-aguinaldo-validar-layout/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (err) {
    onError(err);
    return false;
  }
};
